import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  CurrencyDollarIcon,
  BanknotesIcon,
  CalculatorIcon,
  ClockIcon,
  MapPinIcon,
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { calculatorAPI, destinationsAPI } from '../services/api';
import { loadCustomDestinations, addCustomDestination, CustomDestination } from '../utils/customDestinations';
import { CustomDestinationModal } from '../components/destinations/CustomDestinationModal';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { useSettingsStore } from '../store/settingsStore';

// Tab definitions
type CalculatorTab = 'currency' | 'budget' | 'tip' | 'timezone' | 'distance';

interface TabConfig {
  id: CalculatorTab;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

const TABS: TabConfig[] = [
  { id: 'currency', name: 'Currency Converter', icon: CurrencyDollarIcon, description: 'Convert between world currencies' },
  { id: 'budget', name: 'Trip Budget', icon: BanknotesIcon, description: 'Estimate your trip costs' },
  { id: 'tip', name: 'Tip Calculator', icon: CalculatorIcon, description: 'Tipping customs by country' },
  { id: 'timezone', name: 'Timezone Converter', icon: ClockIcon, description: 'Convert times across timezones' },
  { id: 'distance', name: 'Distance Calculator', icon: MapPinIcon, description: 'Distance between cities' },
];

// Currency options
const CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY', 'SEK', 'NZD',
  'MXN', 'SGD', 'HKD', 'NOK', 'KRW', 'TRY', 'INR', 'RUB', 'BRL', 'ZAR',
  'THB', 'TWD', 'DKK', 'PLN', 'PHP', 'IDR', 'HUF', 'CZK', 'ILS', 'CLP',
  'AED', 'COP', 'SAR', 'MYR', 'RON', 'VND', 'EGP', 'ARS', 'PEN', 'NGN',
];

// Budget styles
const BUDGET_STYLES = [
  { value: 'budget', label: 'Budget' },
  { value: 'mid-range', label: 'Mid-Range' },
  { value: 'luxury', label: 'Luxury' },
];

// Common countries for tip calculator
const TIP_COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'France', 'Germany', 'Italy',
  'Spain', 'Japan', 'China', 'South Korea', 'Australia', 'Brazil', 'Mexico',
  'Thailand', 'India', 'Turkey', 'Egypt', 'Morocco', 'South Africa', 'Argentina',
  'Colombia', 'Peru', 'Chile', 'Netherlands', 'Belgium', 'Switzerland',
  'Austria', 'Greece', 'Portugal', 'Czech Republic', 'Poland', 'Hungary',
  'Sweden', 'Norway', 'Denmark', 'Finland', 'Iceland', 'Ireland',
  'Singapore', 'Malaysia', 'Indonesia', 'Philippines', 'Vietnam',
  'New Zealand', 'Russia', 'Israel', 'UAE', 'Saudi Arabia',
];


// Service type options for tip calculator
const SERVICE_TYPES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'taxi', label: 'Taxi/Rideshare' },
  { value: 'hotel_porter', label: 'Hotel Porter' },
  { value: 'bartender', label: 'Bartender' },
  { value: 'tour_guide', label: 'Tour Guide' },
];

// Travel mode options
const TRAVEL_MODES = [
  { value: 'flight', label: 'Air', field: 'estimated_flight_hours' },
  { value: 'drive', label: 'Car', field: 'estimated_drive_hours' },
  { value: 'rail', label: 'Rail', field: 'estimated_rail_hours' },
  { value: 'bus', label: 'Bus', field: 'estimated_bus_hours' },
  { value: 'cycling', label: 'Bike', field: 'estimated_cycling_hours' },
];

const formatHours = (hours: number): string => {
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`;
  }
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

export const TravelCalculators: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const effectiveUnit = useSettingsStore((s) => s.getEffectiveUnit)();
  const [activeTab, setActiveTab] = useState<CalculatorTab>('currency');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Currency Converter state
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [amount, setAmount] = useState<string>('100');
  const [currencyResult, setCurrencyResult] = useState<any>(null);

  // Trip Budget state
  const [budgetOrigin, setBudgetOrigin] = useState('');
  const [budgetDestination, setBudgetDestination] = useState('');
  const [budgetDays, setBudgetDays] = useState<string>('7');
  const [budgetStyle, setBudgetStyle] = useState('mid-range');
  const [budgetTravelers, setBudgetTravelers] = useState<string>('2');
  const [budgetTravelMode, setBudgetTravelMode] = useState('flight');
  const [budgetResult, setBudgetResult] = useState<any>(null);
  const [budgetTravelTime, setBudgetTravelTime] = useState<any>(null);

  // Tip Calculator state
  const [tipCountry, setTipCountry] = useState('United States');
  const [billAmount, setBillAmount] = useState<string>('50');
  const [tipServiceType, setTipServiceType] = useState('restaurant');
  const [tipCurrency, setTipCurrency] = useState('USD');
  const [tipSplitWays, setTipSplitWays] = useState<string>('1');
  const [tipResult, setTipResult] = useState<any>(null);

  // Timezone Converter state
  const [fromTimezone, setFromTimezone] = useState('America/New_York');
  const [toTimezone, setToTimezone] = useState('Europe/London');
  const [dateTime, setDateTime] = useState<string>(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  });
  const [timezoneResult, setTimezoneResult] = useState<any>(null);

  // Distance Calculator state
  const [fromCity, setFromCity] = useState('');
  const [toCity, setToCity] = useState('');
  const [distanceResult, setDistanceResult] = useState<any>(null);

  // Destination search state (shared for Budget + Timezone)
  const [customDestinations, setCustomDestinations] = useState<CustomDestination[]>([]);
  const [showCustomDestModal, setShowCustomDestModal] = useState(false);

  // Budget destination search
  const [budgetSearchResults, setBudgetSearchResults] = useState<any[]>([]);
  const [budgetSearchOpen, setBudgetSearchOpen] = useState(false);
  const budgetSearchRef = useRef<HTMLDivElement>(null);

  // Timezone destination search (from)
  const [tzFromInput, setTzFromInput] = useState('New York');
  const [tzFromSearchResults, setTzFromSearchResults] = useState<any[]>([]);
  const [tzFromSearchOpen, setTzFromSearchOpen] = useState(false);
  const tzFromSearchRef = useRef<HTMLDivElement>(null);

  // Timezone destination search (to)
  const [tzToInput, setTzToInput] = useState('London');
  const [tzToSearchResults, setTzToSearchResults] = useState<any[]>([]);
  const [tzToSearchOpen, setTzToSearchOpen] = useState(false);
  const tzToSearchRef = useRef<HTMLDivElement>(null);

  // Which field triggered the custom destination modal
  const [customDestTarget, setCustomDestTarget] = useState<'budget' | 'tzFrom' | 'tzTo' | null>(null);

  // Load custom destinations
  useEffect(() => {
    setCustomDestinations(loadCustomDestinations());
  }, []);

  // Click-outside handler for search dropdowns
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (budgetSearchRef.current && !budgetSearchRef.current.contains(e.target as Node)) {
        setBudgetSearchOpen(false);
      }
      if (tzFromSearchRef.current && !tzFromSearchRef.current.contains(e.target as Node)) {
        setTzFromSearchOpen(false);
      }
      if (tzToSearchRef.current && !tzToSearchRef.current.contains(e.target as Node)) {
        setTzToSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track which search fields are focused
  const [budgetFocused, setBudgetFocused] = useState(false);
  const [tzFromFocused, setTzFromFocused] = useState(false);
  const [tzToFocused, setTzToFocused] = useState(false);

  // Debounced search for budget destination
  useEffect(() => {
    if (!budgetDestination.trim() || budgetDestination.includes(',') || !budgetFocused) {
      setBudgetSearchResults([]);
      setBudgetSearchOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const q = budgetDestination.toLowerCase();
        const customMatches = customDestinations
          .filter(cd => cd.city.toLowerCase().includes(q) || cd.country.toLowerCase().includes(q))
          .map(cd => ({ id: cd.id, city: cd.city, country: cd.country, isCustom: true }));
        const data = await destinationsAPI.search({ q: budgetDestination, limit: 6 });
        const apiMatches = (data.results || []).map((d: any) => ({
          id: d.id, city: d.city, country: d.country, isCustom: false,
        }));
        const seenIds = new Set(apiMatches.map((d: any) => d.id));
        const merged = [...apiMatches, ...customMatches.filter(d => !seenIds.has(d.id))];
        setBudgetSearchResults(merged.slice(0, 8));
        setBudgetSearchOpen(merged.length > 0);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [budgetDestination, customDestinations, budgetFocused]);

  // Debounced search for timezone "from" city
  useEffect(() => {
    if (!tzFromInput.trim() || !tzFromFocused) {
      setTzFromSearchResults([]);
      setTzFromSearchOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const q = tzFromInput.toLowerCase();
        const customMatches = customDestinations
          .filter(cd => cd.city.toLowerCase().includes(q) || cd.country.toLowerCase().includes(q))
          .map(cd => ({ id: cd.id, city: cd.city, country: cd.country, isCustom: true }));
        const data = await destinationsAPI.search({ q: tzFromInput, limit: 6 });
        const apiMatches = (data.results || []).map((d: any) => ({
          id: d.id, city: d.city, country: d.country, isCustom: false,
        }));
        const seenIds = new Set(apiMatches.map((d: any) => d.id));
        const merged = [...apiMatches, ...customMatches.filter(d => !seenIds.has(d.id))];
        setTzFromSearchResults(merged.slice(0, 8));
        setTzFromSearchOpen(merged.length > 0);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [tzFromInput, customDestinations, tzFromFocused]);

  // Debounced search for timezone "to" city
  useEffect(() => {
    if (!tzToInput.trim() || !tzToFocused) {
      setTzToSearchResults([]);
      setTzToSearchOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const q = tzToInput.toLowerCase();
        const customMatches = customDestinations
          .filter(cd => cd.city.toLowerCase().includes(q) || cd.country.toLowerCase().includes(q))
          .map(cd => ({ id: cd.id, city: cd.city, country: cd.country, isCustom: true }));
        const data = await destinationsAPI.search({ q: tzToInput, limit: 6 });
        const apiMatches = (data.results || []).map((d: any) => ({
          id: d.id, city: d.city, country: d.country, isCustom: false,
        }));
        const seenIds = new Set(apiMatches.map((d: any) => d.id));
        const merged = [...apiMatches, ...customMatches.filter(d => !seenIds.has(d.id))];
        setTzToSearchResults(merged.slice(0, 8));
        setTzToSearchOpen(merged.length > 0);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [tzToInput, customDestinations, tzToFocused]);

  const handleCurrencyConvert = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount greater than zero.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await calculatorAPI.convertCurrency({
        from_currency: fromCurrency,
        to_currency: toCurrency,
        amount: parseFloat(amount),
      });
      setCurrencyResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to convert currency. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSwapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setCurrencyResult(null);
  };

  const handleBudgetEstimate = async () => {
    if (!budgetDestination.trim()) {
      setError('Please enter a destination.');
      return;
    }
    if (!budgetDays || parseInt(budgetDays) <= 0) {
      setError('Please enter a valid number of days.');
      return;
    }
    if (!budgetTravelers || parseInt(budgetTravelers) <= 0) {
      setError('Please enter a valid number of travelers.');
      return;
    }
    setLoading(true);
    setError(null);
    setBudgetTravelTime(null);
    try {
      // Get budget estimate
      const destCity = budgetDestination.split(',')[0].trim();
      const result = await calculatorAPI.estimateBudget({
        destination: destCity,
        days: parseInt(budgetDays),
        budget_style: budgetStyle,
        travelers: parseInt(budgetTravelers),
      });
      setBudgetResult(result);

      // If origin provided, also calculate travel time
      if (budgetOrigin.trim()) {
        try {
          const originCity = budgetOrigin.split(',')[0].trim();
          const distResult = await calculatorAPI.calculateDistance({
            from_city: originCity,
            to_city: destCity,
          });
          setBudgetTravelTime(distResult);
        } catch {
          // Travel time is optional, don't fail if distance calc fails
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to estimate budget. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTipCalculate = async () => {
    if (!billAmount || parseFloat(billAmount) <= 0) {
      setError('Please enter a valid bill amount greater than zero.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await calculatorAPI.calculateTip({
        country: tipCountry,
        bill_amount: parseFloat(billAmount),
        service_type: tipServiceType,
        currency: tipCurrency,
        split_ways: parseInt(tipSplitWays) || 1,
      });
      setTipResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to calculate tip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneConvert = async () => {
    if (!dateTime) {
      setError('Please select a date and time.');
      return;
    }
    // Use fromTimezone/toTimezone which may be city names or IANA timezones
    const fromTz = fromTimezone || tzFromInput.trim();
    const toTz = toTimezone || tzToInput.trim();
    if (!fromTz) {
      setError('Please select or type a source city/timezone.');
      return;
    }
    if (!toTz) {
      setError('Please select or type a destination city/timezone.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await calculatorAPI.convertTimezone({
        from_timezone: fromTz,
        to_timezone: toTz,
        datetime: dateTime,
      });
      setTimezoneResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to convert timezone. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDistanceCalculate = async () => {
    if (!fromCity.trim()) {
      setError('Please enter a departure city.');
      return;
    }
    if (!toCity.trim()) {
      setError('Please enter a destination city.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await calculatorAPI.calculateDistance({
        from_city: fromCity.trim(),
        to_city: toCity.trim(),
      });
      setDistanceResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to calculate distance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdown = (content: string) => (
    <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-600 dark:text-cyan-400 underline hover:text-cyan-700 dark:hover:text-cyan-300"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="text-gray-900 dark:text-gray-100 font-semibold">{children}</strong>
          ),
          h3: ({ children }) => (
            <h3 className="text-gray-900 dark:text-gray-100 text-base font-semibold mt-4 mb-2">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-gray-900 dark:text-gray-100 text-sm font-semibold mt-3 mb-1">{children}</h4>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );

  const renderCurrencyConverter = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        {/* Amount */}
        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Amount
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => { setAmount(e.target.value); setCurrencyResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="100.00"
          />
        </div>

        {/* From Currency */}
        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            From
          </label>
          <select
            value={fromCurrency}
            onChange={(e) => { setFromCurrency(e.target.value); setCurrencyResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {CURRENCIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Swap Button */}
        <div className="md:col-span-1 flex justify-center">
          <button
            onClick={handleSwapCurrencies}
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 text-gray-600 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
            title="Swap currencies"
          >
            <ArrowsRightLeftIcon className="h-5 w-5" />
          </button>
        </div>

        {/* To Currency */}
        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            To
          </label>
          <select
            value={toCurrency}
            onChange={(e) => { setToCurrency(e.target.value); setCurrencyResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {CURRENCIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Convert Button */}
        <div className="md:col-span-1">
          <button
            onClick={handleCurrencyConvert}
            disabled={loading}
            className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
          >
            Convert
          </button>
        </div>
      </div>

      {/* Currency Result */}
      {currencyResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          {currencyResult.answer ? (
            renderMarkdown(currencyResult.answer)
          ) : (
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {currencyResult.converted_amount !== undefined
                  ? `${parseFloat(amount).toLocaleString()} ${fromCurrency} = ${currencyResult.converted_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${toCurrency}`
                  : 'Conversion complete'}
              </p>
              {currencyResult.exchange_rate && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Exchange rate: 1 {fromCurrency} = {currencyResult.exchange_rate} {toCurrency}
                </p>
              )}
              {currencyResult.date && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Rate as of {currencyResult.date}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderBudgetEstimator = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Origin (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Traveling From <span className="text-gray-400 text-xs">(optional)</span>
          </label>
          <input
            type="text"
            value={budgetOrigin}
            onChange={(e) => { setBudgetOrigin(e.target.value); setBudgetResult(null); setBudgetTravelTime(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="e.g., New York"
          />
        </div>

        {/* Destination with search */}
        <div ref={budgetSearchRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Destination
          </label>
          <input
            type="text"
            value={budgetDestination}
            onChange={(e) => { setBudgetDestination(e.target.value); setBudgetResult(null); }}
            onFocus={() => { setBudgetFocused(true); if (budgetSearchResults.length > 0) setBudgetSearchOpen(true); }}
            onBlur={() => setTimeout(() => setBudgetFocused(false), 200)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Search destinations..."
          />
          {budgetSearchOpen && budgetSearchResults.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {budgetSearchResults.map((d) => (
                <button
                  key={d.id}
                  onClick={() => {
                    setBudgetDestination(`${d.city}, ${d.country}`);
                    setBudgetSearchOpen(false);
                    setBudgetSearchResults([]);
                    setBudgetResult(null);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2"
                >
                  <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span>{d.city}, {d.country}</span>
                  {d.isCustom && (
                    <span className="ml-auto text-xs text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-1.5 py-0.5 rounded">Custom</span>
                  )}
                </button>
              ))}
              <button
                onClick={() => { setCustomDestTarget('budget'); setShowCustomDestModal(true); setBudgetSearchOpen(false); }}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-cyan-600 dark:text-cyan-400 flex items-center gap-2 border-t border-gray-200 dark:border-gray-700"
              >
                <PlusCircleIcon className="h-4 w-4 flex-shrink-0" />
                <span>Add custom destination...</span>
              </button>
            </div>
          )}
        </div>

        {/* Travel Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Travel Mode
          </label>
          <select
            value={budgetTravelMode}
            onChange={(e) => { setBudgetTravelMode(e.target.value); setBudgetResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {TRAVEL_MODES.map(mode => (
              <option key={mode.value} value={mode.value}>{mode.label}</option>
            ))}
          </select>
        </div>

        {/* Number of Days */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Number of Days
          </label>
          <input
            type="number"
            min="1"
            max="365"
            value={budgetDays}
            onChange={(e) => { setBudgetDays(e.target.value); setBudgetResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="7"
          />
        </div>

        {/* Budget Style */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Budget Style
          </label>
          <select
            value={budgetStyle}
            onChange={(e) => { setBudgetStyle(e.target.value); setBudgetResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {BUDGET_STYLES.map(style => (
              <option key={style.value} value={style.value}>{style.label}</option>
            ))}
          </select>
        </div>

        {/* Number of Travelers */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Number of Travelers
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={budgetTravelers}
            onChange={(e) => { setBudgetTravelers(e.target.value); setBudgetResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="2"
          />
        </div>
      </div>

      {/* Estimate Button */}
      <div className="flex justify-center">
        <button
          onClick={handleBudgetEstimate}
          disabled={loading}
          className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          Estimate Budget
        </button>
      </div>

      {/* Budget Result */}
      {budgetResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          {budgetResult.answer ? (
            renderMarkdown(budgetResult.answer)
          ) : (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Estimated Budget for {budgetResult.destination || budgetDestination}
              </h3>

              {/* Travel Time Info */}
              {budgetTravelTime && (
                <div className="mb-4 p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                  <p className="text-sm text-cyan-800 dark:text-cyan-200 font-medium mb-1">
                    Travel from {budgetTravelTime.from_location} to {budgetTravelTime.to_location}
                  </p>
                  <p className="text-sm text-cyan-700 dark:text-cyan-300">
                    Distance: {effectiveUnit === 'miles'
                      ? `${budgetTravelTime.distance_miles?.toLocaleString()} mi`
                      : `${budgetTravelTime.distance_km?.toLocaleString()} km`}
                  </p>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {TRAVEL_MODES.map(mode => {
                      const hours = budgetTravelTime[mode.field];
                      if (hours == null) return null;
                      const isSelected = budgetTravelMode === mode.value;
                      return (
                        <span key={mode.value} className={`text-xs px-2 py-1 rounded ${isSelected ? 'bg-cyan-600 text-white font-semibold' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                          {mode.label}: {formatHours(hours)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Daily per Person</p>
                  <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">
                    ${budgetResult.daily_budget_per_person?.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total per Person</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    ${budgetResult.total_budget_per_person?.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total ({budgetResult.travelers} traveler{budgetResult.travelers > 1 ? 's' : ''})</p>
                  <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">
                    ${budgetResult.total_budget_group?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Breakdown */}
              {budgetResult.breakdown && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Cost Breakdown (per person, {budgetResult.days} days)</h4>
                  {Object.entries(budgetResult.breakdown).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-gray-700 dark:text-gray-300">
                      <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">${(value as number).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Note */}
              {budgetResult.note && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 italic">{budgetResult.note}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderTipCalculator = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Country */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Country
          </label>
          <select
            value={tipCountry}
            onChange={(e) => { setTipCountry(e.target.value); setTipResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {TIP_COUNTRIES.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>

        {/* Service Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Service Type
          </label>
          <select
            value={tipServiceType}
            onChange={(e) => { setTipServiceType(e.target.value); setTipResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {SERVICE_TYPES.map(st => (
              <option key={st.value} value={st.value}>{st.label}</option>
            ))}
          </select>
        </div>

        {/* Bill Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Bill Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">{tipCurrency}</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={billAmount}
              onChange={(e) => { setBillAmount(e.target.value); setTipResult(null); }}
              className="w-full pl-12 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="50.00"
            />
          </div>
        </div>

        {/* Currency */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Currency
          </label>
          <select
            value={tipCurrency}
            onChange={(e) => { setTipCurrency(e.target.value); setTipResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {CURRENCIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Split Ways */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Split Between
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={tipSplitWays}
            onChange={(e) => { setTipSplitWays(e.target.value); setTipResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="1"
          />
        </div>

        {/* Calculate Button */}
        <div className="flex items-end">
          <button
            onClick={handleTipCalculate}
            disabled={loading}
            className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
          >
            Calculate Tip
          </button>
        </div>
      </div>

      {/* Tip Result */}
      {tipResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          {tipResult.answer ? (
            renderMarkdown(tipResult.answer)
          ) : (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Tipping in {tipResult.country || tipCountry}
              </h3>

              {/* Tip Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Tip ({tipResult.tip_percentage}%)</p>
                  <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                    {tipResult.currency} {tipResult.tip_amount?.toFixed(2)}
                  </p>
                </div>
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total with Tip</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {tipResult.currency} {tipResult.total_with_tip?.toFixed(2)}
                  </p>
                </div>
                {tipResult.per_person && (
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Per Person ({tipResult.split_ways} ways)</p>
                    <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                      {tipResult.currency} {tipResult.per_person?.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              {/* Bill Summary */}
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 space-y-1">
                <div className="flex justify-between">
                  <span>Bill Amount:</span>
                  <span>{tipResult.currency} {tipResult.bill_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Type:</span>
                  <span className="capitalize">{tipResult.service_type?.replace(/_/g, ' ')}</span>
                </div>
              </div>

              {/* Cultural Notes */}
              {tipResult.cultural_notes && (
                <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                  <p className="text-sm text-cyan-800 dark:text-cyan-200">
                    <strong>Cultural note:</strong> {tipResult.cultural_notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const handleSwapTimezoneInputs = () => {
    const tmpTz = fromTimezone;
    const tmpInput = tzFromInput;
    setFromTimezone(toTimezone);
    setToTimezone(tmpTz);
    setTzFromInput(tzToInput);
    setTzToInput(tmpInput);
    setTimezoneResult(null);
  };

  const renderTimezoneConverter = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Date/Time */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date & Time
          </label>
          <input
            type="datetime-local"
            value={dateTime}
            onChange={(e) => { setDateTime(e.target.value); setTimezoneResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>

        {/* From City/Timezone */}
        <div ref={tzFromSearchRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            From (City or Timezone)
          </label>
          <input
            type="text"
            value={tzFromInput}
            onChange={(e) => { setTzFromInput(e.target.value); setFromTimezone(e.target.value); setTimezoneResult(null); }}
            onFocus={() => { setTzFromFocused(true); if (tzFromSearchResults.length > 0) setTzFromSearchOpen(true); }}
            onBlur={() => setTimeout(() => setTzFromFocused(false), 200)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Search city or type timezone..."
          />
          {fromTimezone && tzFromInput && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Using: {fromTimezone}</p>
          )}
          {tzFromSearchOpen && tzFromSearchResults.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {tzFromSearchResults.map((d) => (
                <button
                  key={d.id}
                  onClick={() => {
                    setTzFromInput(d.city);
                    setFromTimezone(d.city);
                    setTzFromSearchOpen(false);
                    setTzFromSearchResults([]);
                    setTimezoneResult(null);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2"
                >
                  <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span>{d.city}, {d.country}</span>
                  {d.isCustom && (
                    <span className="ml-auto text-xs text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-1.5 py-0.5 rounded">Custom</span>
                  )}
                </button>
              ))}
              <button
                onClick={() => { setCustomDestTarget('tzFrom'); setShowCustomDestModal(true); setTzFromSearchOpen(false); }}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-cyan-600 dark:text-cyan-400 flex items-center gap-2 border-t border-gray-200 dark:border-gray-700"
              >
                <PlusCircleIcon className="h-4 w-4 flex-shrink-0" />
                <span>Add custom destination...</span>
              </button>
            </div>
          )}
        </div>

        {/* To City/Timezone */}
        <div ref={tzToSearchRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            To (City or Timezone)
          </label>
          <input
            type="text"
            value={tzToInput}
            onChange={(e) => { setTzToInput(e.target.value); setToTimezone(e.target.value); setTimezoneResult(null); }}
            onFocus={() => { setTzToFocused(true); if (tzToSearchResults.length > 0) setTzToSearchOpen(true); }}
            onBlur={() => setTimeout(() => setTzToFocused(false), 200)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Search city or type timezone..."
          />
          {toTimezone && tzToInput && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Using: {toTimezone}</p>
          )}
          {tzToSearchOpen && tzToSearchResults.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {tzToSearchResults.map((d) => (
                <button
                  key={d.id}
                  onClick={() => {
                    setTzToInput(d.city);
                    setToTimezone(d.city);
                    setTzToSearchOpen(false);
                    setTzToSearchResults([]);
                    setTimezoneResult(null);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2"
                >
                  <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span>{d.city}, {d.country}</span>
                  {d.isCustom && (
                    <span className="ml-auto text-xs text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-1.5 py-0.5 rounded">Custom</span>
                  )}
                </button>
              ))}
              <button
                onClick={() => { setCustomDestTarget('tzTo'); setShowCustomDestModal(true); setTzToSearchOpen(false); }}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm text-cyan-600 dark:text-cyan-400 flex items-center gap-2 border-t border-gray-200 dark:border-gray-700"
              >
                <PlusCircleIcon className="h-4 w-4 flex-shrink-0" />
                <span>Add custom destination...</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-3">
        <button
          onClick={handleSwapTimezoneInputs}
          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 text-gray-700 dark:text-gray-300 hover:text-cyan-700 dark:hover:text-cyan-400 font-medium rounded-lg transition-colors flex items-center gap-2"
        >
          <ArrowsRightLeftIcon className="h-4 w-4" />
          Swap
        </button>
        <button
          onClick={handleTimezoneConvert}
          disabled={loading}
          className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          Convert Time
        </button>
      </div>

      {/* Timezone Result */}
      {timezoneResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          {timezoneResult.answer ? (
            renderMarkdown(timezoneResult.answer)
          ) : (
            <div className="text-center">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{timezoneResult.from_timezone}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {timezoneResult.from_time || dateTime.replace('T', ' ')}
                  </p>
                  {timezoneResult.from_date && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{timezoneResult.from_date}</p>
                  )}
                </div>
                <div className="flex justify-center">
                  <ArrowsRightLeftIcon className="h-6 w-6 text-cyan-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{timezoneResult.to_timezone}</p>
                  <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                    {timezoneResult.to_time || 'N/A'}
                  </p>
                  {timezoneResult.to_date && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{timezoneResult.to_date}</p>
                  )}
                </div>
              </div>
              {timezoneResult.difference && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                  Time difference: {timezoneResult.difference}
                </p>
              )}
              {timezoneResult.jet_lag_note && (
                <p className="text-sm text-cyan-600 dark:text-cyan-400 mt-2">
                  {timezoneResult.jet_lag_note}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderDistanceCalculator = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        {/* From City */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            From
          </label>
          <input
            type="text"
            value={fromCity}
            onChange={(e) => { setFromCity(e.target.value); setDistanceResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="e.g., New York"
          />
        </div>

        {/* Swap Button */}
        <div className="md:col-span-1 flex justify-center">
          <button
            onClick={() => { const tmp = fromCity; setFromCity(toCity); setToCity(tmp); setDistanceResult(null); }}
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 text-gray-600 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
            title="Swap cities"
          >
            <ArrowsRightLeftIcon className="h-5 w-5" />
          </button>
        </div>

        {/* To City */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            To
          </label>
          <input
            type="text"
            value={toCity}
            onChange={(e) => { setToCity(e.target.value); setDistanceResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="e.g., London"
          />
        </div>
      </div>

      {/* Calculate Button */}
      <div className="flex justify-center">
        <button
          onClick={handleDistanceCalculate}
          disabled={loading}
          className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          Calculate Distance
        </button>
      </div>

      {/* Distance Result */}
      {distanceResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          {distanceResult.answer ? (
            renderMarkdown(distanceResult.answer)
          ) : (
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                {distanceResult.from_location || fromCity} to {distanceResult.to_location || toCity}
              </p>
              {distanceResult.distance_km != null && (
                <>
                  <p className="text-3xl font-bold text-cyan-600 dark:text-cyan-400 mb-1">
                    {effectiveUnit === 'miles'
                      ? `${distanceResult.distance_miles?.toLocaleString()} miles`
                      : `${distanceResult.distance_km.toLocaleString()} km`}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    ({effectiveUnit === 'miles'
                      ? `${distanceResult.distance_km.toLocaleString()} km`
                      : `${distanceResult.distance_miles?.toLocaleString()} miles`})
                  </p>
                </>
              )}

              {/* Travel Time by Mode */}
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Estimated Travel Times</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {distanceResult.estimated_flight_hours != null && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Air</p>
                    <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">
                      {formatHours(distanceResult.estimated_flight_hours)}
                    </p>
                  </div>
                )}
                {distanceResult.estimated_drive_hours != null && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Car</p>
                    <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">
                      {formatHours(distanceResult.estimated_drive_hours)}
                    </p>
                  </div>
                )}
                {distanceResult.estimated_rail_hours != null && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Rail</p>
                    <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">
                      {formatHours(distanceResult.estimated_rail_hours)}
                    </p>
                  </div>
                )}
                {distanceResult.estimated_bus_hours != null && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Bus</p>
                    <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">
                      {formatHours(distanceResult.estimated_bus_hours)}
                    </p>
                  </div>
                )}
                {distanceResult.estimated_cycling_hours != null && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Bike</p>
                    <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">
                      {formatHours(distanceResult.estimated_cycling_hours)}
                    </p>
                  </div>
                )}
              </div>
              {distanceResult.estimated_drive_hours == null && distanceResult.estimated_rail_hours == null && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 italic">
                  Ground travel times only shown for distances under {effectiveUnit === 'miles' ? '1,864 miles' : '3,000 km'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'currency': return renderCurrencyConverter();
      case 'budget': return renderBudgetEstimator();
      case 'tip': return renderTipCalculator();
      case 'timezone': return renderTimezoneConverter();
      case 'distance': return renderDistanceCalculator();
      default: return null;
    }
  };

  return (
    <div className={`flex flex-col ${isMobile ? '' : 'h-full'}`}>
      {/* Header */}
      <div className="flex-none p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
          Travel Calculators
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Essential tools for planning your trips
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex-none border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <div className="flex min-w-max">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setError(null); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="hidden sm:inline">{tab.name}</span>
                <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Description */}
      <div className="flex-none px-4 sm:px-6 py-3 bg-cyan-50 dark:bg-cyan-900/10">
        <p className="text-sm text-cyan-800 dark:text-cyan-200">
          {TABS.find(t => t.id === activeTab)?.description}
        </p>
      </div>

      {/* Content Area */}
      <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto'} p-4 sm:p-6`}>
        {/* Error Message */}
        {error && (
          <div className="mb-4 flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {renderActiveTab()}

        {/* Disclaimer */}
        <p className="mt-6 text-xs text-gray-400 dark:text-gray-500 text-center">
          {t('common:disclaimer.travel', 'These calculations are provided for informational purposes only. Exchange rates, costs, and travel times may vary. Always verify details with official sources before making travel decisions.')}
        </p>
      </div>

      <LoadingOverlay isVisible={loading} message="Calculating..." subMessage="Processing your request" />

      {/* Custom Destination Modal */}
      <CustomDestinationModal
        isOpen={showCustomDestModal}
        onClose={() => { setShowCustomDestModal(false); setCustomDestTarget(null); }}
        onSave={(dest) => {
          const saved = addCustomDestination(dest);
          setCustomDestinations(loadCustomDestinations());
          if (customDestTarget === 'budget') {
            setBudgetDestination(`${saved.city}, ${saved.country}`);
            setBudgetResult(null);
          } else if (customDestTarget === 'tzFrom') {
            setTzFromInput(saved.city);
            setFromTimezone(saved.city);
            setTimezoneResult(null);
          } else if (customDestTarget === 'tzTo') {
            setTzToInput(saved.city);
            setToTimezone(saved.city);
            setTimezoneResult(null);
          }
          setShowCustomDestModal(false);
          setCustomDestTarget(null);
        }}
      />
    </div>
  );
};

export default TravelCalculators;
